import { Badge, Button } from '@/components/ui';
import { sysadminService, type PruebaAudiosActaResponse } from '@/services/sysadmin.service';
import { AlertTriangle, ExternalLink, FileAudio, Loader2, Trash2 } from 'lucide-react';
import { useState } from 'react';

interface PruebaHecha extends PruebaAudiosActaResponse {
  eliminada?: boolean;
}

export function PruebaActaAudiosPage() {
  const [asesoriaInput, setAsesoriaInput] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [pruebas, setPruebas] = useState<PruebaHecha[]>([]);
  const [eliminando, setEliminando] = useState<string | null>(null);

  const asesoriaId = Number(asesoriaInput);
  const valido = Number.isInteger(asesoriaId) && asesoriaId > 0;

  const handleProbar = async () => {
    if (!valido) return;
    setIsProcessing(true);
    setError(null);
    try {
      const res = await sysadminService.probarAudiosEnActa(asesoriaId);
      setPruebas((prev) => [res, ...prev]);
    } catch (err: any) {
      setError(err.message || 'No se pudo crear la copia de prueba');
    } finally {
      setIsProcessing(false);
    }
  };

  const handleEliminar = async (fileId: string) => {
    setEliminando(fileId);
    try {
      await sysadminService.eliminarCopiaPruebaActa(fileId);
      setPruebas((prev) => prev.map((p) => (p.copia.id === fileId ? { ...p, eliminada: true } : p)));
    } catch (err: any) {
      setError(err.message || 'No se pudo eliminar la copia');
    } finally {
      setEliminando(null);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-1.5">
        <h1 className="text-xl font-bold text-gray-900">Prueba de Actas: Fila de Audios</h1>
        <p className="text-sm text-gray-500">
          Copia el acta real de una asesoría (con sus ediciones a mano) en su misma carpeta, con el prefijo "PRUEBA AUDIOS -", y le agrega
          la fila AUDIOS con el mismo código que usa la generación de actas. No se modifica el acta original ni la base de datos: abre las
          dos y compáralas.
        </p>
      </div>

      <div className="p-6 bg-white rounded-xl shadow-sm border border-gray-200">
        <div className="flex flex-col md:flex-row md:items-end gap-4">
          <div className="flex flex-col gap-1.5 md:w-64">
            <label className="text-xs font-bold text-gray-400 uppercase tracking-wider px-1">ID de asesoría</label>
            <input
              type="number"
              min={1}
              value={asesoriaInput}
              onChange={(e) => setAsesoriaInput(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleProbar()}
              disabled={isProcessing}
              placeholder="Ej: 502"
              className="w-full bg-white border border-gray-200 rounded-lg py-2 px-3 text-sm text-gray-700 focus:ring-2 focus:ring-blue-500 outline-none transition-all disabled:opacity-50"
            />
          </div>
          <Button onClick={handleProbar} disabled={!valido || isProcessing}>
            {isProcessing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <FileAudio className="w-4 h-4 mr-2" />}
            {isProcessing ? 'Creando copia...' : 'Crear copia de prueba'}
          </Button>
        </div>

        {error && (
          <div className="mt-4 flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
            <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
            <span>{error}</span>
          </div>
        )}
      </div>

      {pruebas.map((p) => (
        <div key={p.copia.id} className="p-5 bg-white rounded-xl shadow-sm border border-gray-200 space-y-4">
          <div className="flex flex-col md:flex-row md:items-start justify-between gap-3">
            <div>
              <div className="flex items-center gap-2">
                <h2 className="text-sm font-bold text-gray-900">Asesoría #{p.asesoriaId}</h2>
                {p.eliminada ? (
                  <Badge variant="gray">Copia eliminada</Badge>
                ) : p.filaInsertada ? (
                  <Badge variant="green">Fila insertada</Badge>
                ) : (
                  <Badge variant="warning">Sin cambios</Badge>
                )}
              </div>
              <div className="mt-2 flex flex-col gap-1 text-xs">
                <a
                  href={p.actaOriginal.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-blue-600 hover:underline inline-flex items-center gap-1"
                >
                  Original: {p.actaOriginal.nombre} <ExternalLink className="w-3 h-3" />
                </a>
                {!p.eliminada && (
                  <a
                    href={p.copia.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-600 hover:underline inline-flex items-center gap-1"
                  >
                    Copia: {p.copia.nombre} <ExternalLink className="w-3 h-3" />
                  </a>
                )}
              </div>
            </div>
            {!p.eliminada && (
              <Button variant="outline" size="sm" onClick={() => handleEliminar(p.copia.id)} disabled={eliminando === p.copia.id}>
                {eliminando === p.copia.id ? (
                  <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />
                ) : (
                  <Trash2 className="w-3.5 h-3.5 mr-1.5" />
                )}
                Eliminar copia
              </Button>
            )}
          </div>

          {p.advertencias.length > 0 && (
            <ul className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800 space-y-0.5">
              {p.advertencias.map((a) => (
                <li key={a} className="flex items-start gap-1.5">
                  <AlertTriangle className="w-3.5 h-3.5 mt-px shrink-0" /> {a}
                </li>
              ))}
            </ul>
          )}

          <div>
            <p className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-1.5">Fila que se agregó</p>
            <table className="w-full max-w-xl text-xs border border-gray-200">
              <tbody className="divide-y divide-gray-200">
                {p.audios.map((a) => (
                  <tr key={a.audioId}>
                    <td className="px-3 py-1.5 border-r border-gray-200">
                      <a href={a.url} target="_blank" rel="noopener noreferrer" className="font-mono text-blue-600 hover:underline">
                        {a.nombre}
                      </a>
                      {!a.formatoValido && <span className="ml-2 text-[10px] text-red-600">(formato inválido)</span>}
                    </td>
                    <td className="px-3 py-1.5 font-mono">{a.duracion}</td>
                  </tr>
                ))}
                <tr className="bg-gray-50">
                  <td className="px-3 py-1.5 border-r border-gray-200 font-bold">TOTAL</td>
                  <td className="px-3 py-1.5 font-mono font-semibold">{p.total}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      ))}
    </div>
  );
}
